import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, User, Users, Briefcase, Phone, Building2 } from "lucide-react";
import { trackSignUp, trackFormInteraction } from "@/utils/analytics";

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [registrationType, setRegistrationType] = useState("community");
  const [form, setForm] = useState({ 
    email: "", 
    password: "", 
    name: "",
    phone: "",
    business_name: "",
    role: "client"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          ...form,
          registration_type: registrationType
        })
      });
      
      if (response.ok) {
        const userData = await response.json();
        
        // Track successful sign up
        trackSignUp(registrationType);
        
        // Store token first to ensure it's available for subsequent requests
        if (userData.token) {
          try {
            localStorage.setItem("auth_token", userData.token);
          } catch (storageErr) {
            console.error("Failed to store token:", storageErr);
          }
        }
        
        login(userData);
        
        // Small delay to ensure state is synced before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (registrationType === "community") {
          toast.success("Welcome to the community!");
          navigate("/community");
        } else {
          toast.success("Account created! Please complete onboarding.");
          navigate("/onboarding");
        }
      } else {
        const errorData = await response.json();
        trackFormInteraction('registration', 'error');
        toast.error(errorData.detail || "Registration failed");
      }
    } catch (_err) {
      console.error("Registration error:", _err);
      toast.error("Unable to connect. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#333333] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="inline-flex items-center space-x-2 text-white/80 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span>Back to home</span>
          </Link>
        </div>
        
        <div className="space-y-6">
          <h1 className="text-4xl font-semibold text-white leading-tight">
            Begin Your Journey to
            <span className="block text-[#39FF14]">Business Success</span>
          </h1>
          <p className="text-neutral-300 text-lg max-w-md">
            Join business owners who have found clarity, accountability, and growth.
          </p>
          <ul className="space-y-3 text-neutral-300">
            <li className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-[#39FF14] rounded-full"></div>
              <span>Weekly accountability tracking</span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-[#39FF14] rounded-full"></div>
              <span>AI-powered mindset support</span>
            </li>
            <li className="flex items-center space-x-2">
              <div className="w-1.5 h-1.5 bg-[#39FF14] rounded-full"></div>
              <span>Strategic planning tools</span>
            </li>
          </ul>
        </div>
        
        <div className="flex items-center space-x-3">
          <img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-10 w-auto" />
          <div>
            <p className="text-sm font-semibold text-white leading-tight">My Biz Consulting</p>
            <p className="text-[10px] text-[#39FF14] font-medium uppercase tracking-widest">Business Solutions Center</p>
          </div>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-center space-x-2 text-neutral-600 hover:text-neutral-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span>Back to home</span>
            </Link>
          </div>
          
          <div className="text-center lg:text-left">
            <h2 className="text-3xl font-semibold text-neutral-900 mb-2">Create Account</h2>
            <p className="text-neutral-600">Join our community or become a consulting client</p>
          </div>

          {/* Registration Type Toggle */}
          <Tabs value={registrationType} onValueChange={setRegistrationType} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="community" className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Community
              </TabsTrigger>
              <TabsTrigger value="client" className="flex items-center">
                <Briefcase className="w-4 h-4 mr-2" />
                Full Client
              </TabsTrigger>
            </TabsList>
            <TabsContent value="community" className="mt-3">
              <p className="text-sm text-neutral-500 text-center">
                Free access to workshops, events, and community resources.
              </p>
            </TabsContent>
            <TabsContent value="client" className="mt-3">
              <p className="text-sm text-neutral-500 text-center">
                Full consulting access with AI tools, planning, and 1-on-1 support.
                <br /><span className="text-amber-600">Requires consultant approval.</span>
              </p>
            </TabsContent>
          </Tabs>

          <Button 
            variant="outline" 
            className="w-full h-12 border-neutral-300 hover:bg-neutral-50"
            onClick={handleGoogleLogin}
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-neutral-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-neutral-50 px-4 text-neutral-500">or register with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-neutral-700">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="name"
                  type="text"
                  placeholder="John Smith"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-neutral-700">Phone <span className="text-neutral-400">(optional)</span></Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  data-testid="register-phone-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-neutral-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 h-12 bg-white border-neutral-200 focus:border-[#39FF14] focus:ring-[#39FF14]"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 bg-[#333333] hover:bg-[#444444] text-white font-medium"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>
          </form>

          <p className="text-center text-neutral-600">
            Already have an account?{" "}
            <Link to="/login" className="text-[#39FF14] hover:text-[#32E010] font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
